import React, { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setInView(true), io.disconnect())),
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const reveal = (inView, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(12px)",
  transition: `opacity 480ms ease ${delay}ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
});

const muted = "rgba(26,26,25,0.55)";
const body = "rgba(26,26,25,0.78)";

function Section({ title, children, testid }) {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className="border-t pt-12 sm:pt-14"
      style={{ borderColor: "rgba(26,26,25,0.10)" }}
      data-testid={testid}
    >
      <h2
        className="font-display"
        style={{ fontSize: "clamp(1.25rem, 1.7vw, 1.625rem)", fontWeight: 500, letterSpacing: "-0.005em", color: "#0E0E0D", ...reveal(inView, 0) }}
      >
        {title}
      </h2>
      <div
        className="mt-6 text-base sm:text-[16.5px] leading-[1.85] max-w-[640px] space-y-5"
        style={{ color: body, ...reveal(inView, 120) }}
      >
        {children}
      </div>
    </section>
  );
}

export default function ExchangesReturns() {
  const [topRef, topInView] = useInView();

  return (
    <main
      ref={topRef}
      className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-16 sm:pt-20 pb-24 sm:pb-32"
      data-testid="exchanges-returns-page"
    >
      {/* Header */}
      <div className="max-w-[820px]">
        <h1
          className="font-display leading-[1.05]"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.25rem)",
            letterSpacing: "-0.005em",
            fontWeight: 400,
            ...reveal(topInView, 0),
          }}
          data-testid="er-heading"
        >
          Exchanges, Returns & Refunds <span className="italic">Policy</span>
        </h1>
        <p
          className="mt-10 sm:mt-12 text-base sm:text-[17px] leading-[1.85]"
          style={{ color: body, ...reveal(topInView, 160), maxWidth: "600px" }}
          data-testid="er-intro"
        >
          We hope you love your Tamoré piece. If you require a different size, we're here to help.
        </p>
      </div>

      {/* Summary card */}
      <aside
        className="mt-12 sm:mt-14 px-8 sm:px-10 py-9 sm:py-10 max-w-[760px]"
        style={{ background: "#F4F0E8", ...reveal(topInView, 280) }}
        data-testid="er-summary-card"
      >
        <h2
          className="font-display"
          style={{ fontSize: "clamp(1.375rem, 2vw, 1.625rem)", fontWeight: 400, letterSpacing: "-0.005em" }}
        >
          48-Hour Size Exchange Available
        </h2>
        <p
          className="mt-5 text-sm sm:text-[14.5px] leading-[1.7]"
          style={{ color: body }}
        >
          One exchange per order &nbsp;·&nbsp; Customer pays return shipping &nbsp;·&nbsp; Tamoré pays reshipment &nbsp;·&nbsp; No monetary refunds
        </p>
      </aside>

      <div className="mt-20 sm:mt-24 space-y-16 sm:space-y-20">
        <Section title="Key Information" testid="er-key-information">
          <ul className="list-none space-y-3">
            <li>· As a small-batch label, quantities are limited and exchange requests are subject to stock availability.</li>
            <li>· We do not offer monetary refunds.</li>
            <li>· Only one exchange is permitted per order.</li>
            <li>· Exchange requests must be initiated within 48 hours of delivery.</li>
            <li>· Customers are responsible for shipping the product back to Tamoré, while Tamoré bears the cost of dispatching the exchanged item.</li>
            <li>· If the requested size is unavailable, customers may choose another available product of equal value, another available product of higher value by paying the difference, or receive store credit equivalent to the purchase value.</li>
          </ul>
        </Section>

        <Section title="Exchanges" testid="er-exchanges">
          <p>
            We accept size exchange requests submitted within 48 hours of delivery, subject to
            product availability.
          </p>
          <p>
            To initiate an exchange, please email{" "}
            <a
              href="mailto:support@tamorebytamanna.com"
              className="underline underline-offset-4 hover:opacity-70"
              style={{ color: "#1A1A19" }}
            >
              support@tamorebytamanna.com
            </a>{" "}
            with your order number and reason for exchange.
          </p>
        </Section>

        <Section title="Eligibility" testid="er-eligibility">
          <p>To qualify for an exchange, the product must:</p>
          <ul className="list-none space-y-2">
            <li>· Be unworn, unused, and unwashed.</li>
            <li>· Be returned in its original condition.</li>
            <li>· Have all original tags, labels, and packaging intact.</li>
            <li>· Be free from stains, makeup marks, perfume, deodorant marks, alterations, damage, or any signs of wear.</li>
          </ul>
          <p>Products that do not meet these requirements will not be eligible for exchange.</p>
        </Section>

        <Section title="Exchange Process" testid="er-process">
          <p>Once your exchange request has been reviewed and approved, we will share the return instructions.</p>
          <p>Upon receiving the product, our team will conduct a quality inspection. We aim to complete this review within 48 hours of receipt.</p>
          <p>If the product passes inspection, the exchange will be processed subject to stock availability.</p>
        </Section>

        <Section title="Shipping" testid="er-shipping">
          <ul className="list-none space-y-2">
            <li>· Customers are responsible for the cost of shipping the product back to Tamoré.</li>
            <li>· Tamoré will bear the shipping cost of dispatching the exchanged item.</li>
          </ul>
        </Section>

        <Section title="Stock Availability" testid="er-stock">
          <p>As our products are produced in limited quantities, availability cannot be guaranteed.</p>
          <p>If your requested size is unavailable at the time of processing, you may:</p>
          <ul className="list-none space-y-2">
            <li>· Select another available product of equal value.</li>
            <li>· Select another available product of higher value by paying the difference.</li>
            <li>· Receive store credit equivalent to the purchase value.</li>
          </ul>
        </Section>

        <Section title="Quality Review" testid="er-quality">
          <p>All returned products undergo a quality inspection before an exchange is approved.</p>
          <p>If a returned item does not meet the eligibility requirements outlined above, Tamoré reserves the right to decline the exchange request.</p>
          <p>In such cases:</p>
          <ul className="list-none space-y-2">
            <li>· The item will be returned to the customer.</li>
            <li>· Shipping charges for returning the item will be borne by the customer.</li>
            <li>· No exchange, refund, or store credit will be issued.</li>
          </ul>
        </Section>

        <Section title="Returns & Refunds" testid="er-refunds">
          <p>We currently do not offer returns or monetary refunds.</p>
        </Section>

        <Section title="Damaged, Defective or Incorrect Items" testid="er-damaged">
          <p>
            If you receive a damaged, defective, or incorrect item, please contact{" "}
            <a
              href="mailto:support@tamorebytamanna.com"
              className="underline underline-offset-4 hover:opacity-70"
              style={{ color: "#1A1A19" }}
            >
              support@tamorebytamanna.com
            </a>{" "}
            within 48 hours of delivery and include clear photographs of the issue.
          </p>
          <p>Upon verification, Tamoré will arrange a replacement and bear all associated shipping costs.</p>
        </Section>

        <Section title="Order Cancellations" testid="er-cancellations">
          <p>As orders are processed promptly to ensure timely dispatch, we are unable to accommodate cancellations or modifications once an order has been placed.</p>
          <p>We kindly request customers to review all order details carefully before completing their purchase.</p>
        </Section>

        <Section title="Customised Items" testid="er-customised">
          <p>Any customised, altered, made-to-order, or specially requested products are not eligible for exchange, return, refund, or store credit.</p>
        </Section>

        <Section title="Sizing" testid="er-sizing">
          <p>Please refer to the Size Guide available on our website before placing your order.</p>
        </Section>
      </div>

      {/* Need Assistance closing block */}
      <section
        className="mt-28 sm:mt-36 px-8 sm:px-12 py-14 sm:py-16 text-center"
        style={{ background: "#F4F0E8" }}
        data-testid="er-need-assistance"
      >
        <h2
          className="font-display"
          style={{ fontSize: "clamp(1.5rem, 2.25vw, 2rem)", fontWeight: 400, letterSpacing: "-0.005em", color: "#0E0E0D" }}
        >
          Need Assistance?
        </h2>
        <p
          className="mt-6 text-base sm:text-[17px] leading-[1.85] mx-auto"
          style={{ color: body, maxWidth: "560px" }}
        >
          For questions regarding sizing, exchanges, or your order, please contact{" "}
          <a
            href="mailto:support@tamorebytamanna.com"
            className="underline underline-offset-4 hover:opacity-70"
            style={{ color: "#0E0E0D" }}
          >
            support@tamorebytamanna.com
          </a>
          .
        </p>
        <p className="mt-4 text-sm" style={{ color: muted }}>
          We aim to respond within 24–48 hours.
        </p>
      </section>
    </main>
  );
}
